import React, { useState, useRef, useEffect, useCallback } from 'react';
import { searchSymbols } from '../api';

/**
 * Live ticker typeahead. As the user types we hit `/symbol-search/` (alias table
 * + Yahoo autocomplete) and show a dropdown of real, tradeable symbols so they
 * pick one instead of typing junk that only fails at run time (the #1 source of
 * dead first-run backtests — futures roots like NQ/MNQ, broker names like US30).
 *
 * Controlled input: parent owns `value`/`onChange` (so its existing submit-time
 * validation still runs as a paste-and-go fallback). `onPick(symbol, assetType)`
 * fires when the user selects a suggestion. `classes` (CSV, e.g. "stock,fx")
 * restricts asset classes, mirroring validateSymbol.
 *
 * Keyboard: ↑/↓ move, Enter selects the highlighted row (or submits if none),
 * Esc closes. Closes on outside click/blur. Works the same on touch — rows are
 * sized for taps and the list scrolls within a capped height on small screens.
 */
const QT_STYLE = {
  Future:  { bg: 'rgba(219,109,40,0.16)', fg: '#db6d28' },
  Index:   { bg: 'rgba(88,166,255,0.16)', fg: '#58a6ff' },
  FX:      { bg: 'rgba(63,185,80,0.16)',  fg: '#3fb950' },
  Crypto:  { bg: 'rgba(219,171,40,0.16)', fg: '#dbab28' },
  ETF:     { bg: 'rgba(139,148,158,0.16)', fg: '#8b949e' },
  Stock:   { bg: 'rgba(139,148,158,0.16)', fg: '#8b949e' },
};

export default function TickerAutocomplete({
  value,
  onChange,
  onPick,
  classes,
  placeholder,
  style,
  invalidStyle,
  invalid = false,
  inputRef,
  onKeyDownExtra,
  wrapperStyle,
  menuMinWidth,
  menuAlign = 'left',
  ...inputProps
}) {
  const menuSide = menuAlign === 'right' ? { right: 0, left: 'auto' } : { left: 0, right: 0 };
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const seq = useRef(0);
  const wrapRef = useRef(null);
  const listboxId = useRef(`ticker-ac-${Math.random().toString(36).slice(2, 9)}`).current;
  // The value we just applied via a pick — suppresses reopening the dropdown when
  // the parent echoes that value back through `value`.
  const lastPicked = useRef(null);

  // Debounced live search as the user types.
  useEffect(() => {
    const q = (value || '').trim();
    if (lastPicked.current !== null && q === lastPicked.current) return;
    lastPicked.current = null;
    if (q.length < 1) { setResults([]); setOpen(false); setLoading(false); return undefined; }
    const s = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      searchSymbols(q, classes)
        .then(({ data }) => {
          if (s !== seq.current) return;
          const rows = data?.results || [];
          setResults(rows);
          setActive(-1);
          setOpen(rows.length > 0);
        })
        .catch(() => { if (s === seq.current) { setResults([]); setOpen(false); } })
        .finally(() => { if (s === seq.current) setLoading(false); });
    }, 220);
    return () => clearTimeout(t);
  }, [value, classes]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = useCallback((row) => {
    if (!row) return;
    lastPicked.current = row.symbol;
    onChange(row.symbol);
    onPick?.(row.symbol, row.asset_type);
    setOpen(false);
    setResults([]);
    setActive(-1);
  }, [onChange, onPick]);

  const onKeyDown = (e) => {
    if (open && results.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % results.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a <= 0 ? results.length - 1 : a - 1)); return; }
      if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(results[active]); return; }
      if (e.key === 'Escape') { setOpen(false); return; }
    }
    onKeyDownExtra?.(e);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...wrapperStyle }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
        style={invalid && invalidStyle ? { ...style, ...invalidStyle } : style}
        {...inputProps}
      />
      {open && results.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="nodrag nopan"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', ...menuSide, zIndex: 1000,
            margin: 0, padding: 4, listStyle: 'none',
            background: 'var(--panel, #161b22)',
            border: '1px solid var(--border, #30363d)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            maxHeight: 280, overflowY: 'auto',
            ...(menuMinWidth ? { minWidth: menuMinWidth } : {}),
          }}
        >
          {results.map((r, i) => {
            const badge = QT_STYLE[r.quote_type] || QT_STYLE.Stock;
            return (
              <li
                key={`${r.symbol}-${r.asset_type}`}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 10px', borderRadius: 6, cursor: 'pointer',
                  minHeight: 40,
                  background: i === active ? 'var(--hover, rgba(255,255,255,0.06))' : 'transparent',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{r.symbol}</span>
                {r.quote_type && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
                    background: badge.bg, color: badge.fg, whiteSpace: 'nowrap',
                  }}>{r.quote_type}</span>
                )}
                {r.name && (
                  <span style={{
                    fontSize: 11, color: 'var(--muted, #8b949e)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{r.name}</span>
                )}
                {r.exchange && (
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted, #8b949e)', whiteSpace: 'nowrap' }}>
                    {r.exchange}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {open && loading && !results.length && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', ...menuSide, zIndex: 1000,
          padding: '8px 10px', fontSize: 11, color: 'var(--muted, #8b949e)',
          background: 'var(--panel, #161b22)', border: '1px solid var(--border, #30363d)', borderRadius: 8,
          ...(menuMinWidth ? { minWidth: menuMinWidth } : {}),
        }}>Searching…</div>
      )}
    </div>
  );
}
